/**
 * nuqs Adapter for BYOS
 *
 * This adapter uses nuqs for URL-based state management.
 * It supports SSR and URL synchronization.
 */

import { useQueryStates, type ParserBuilder } from 'nuqs'
import { useEffect, useMemo, useRef } from 'react'
import type { InternalStoreAdapter } from '../../adapter/types'
import { getSchemaDefaults, validateState } from '../../schema/serialization'
import type { SchemaDefinition, StoreSnapshot } from '../../schema/types'
import { schemaToNuqsParsers } from './parser-bridge'
import type { NuqsAdapterOptions } from './types'

export type { NuqsAdapterOptions } from './types'

/**
 * Create a nuqs adapter for URL-based state management
 *
 * @example
 * ```typescript
 * const schema = createSchema({
 *   regions: field.array(field.string()).default([]),
 *   host: field.string(),
 * });
 *
 * function MyComponent() {
 *   const adapter = useNuqsAdapter(schema.definition, { id: 'my-table' });
 *   return (
 *     <DataTableStoreProvider adapter={adapter}>
 *       <DataTable />
 *     </DataTableStoreProvider>
 *   );
 * }
 * ```
 */
export function useNuqsAdapter<T extends Record<string, unknown>>(
  schema: SchemaDefinition,
  options: NuqsAdapterOptions<T>
): InternalStoreAdapter<T> {
  const {
    id,
    initialState,
    // No default: the app-wide `shallow` default lives on <NuqsAdapter> in
    // App.tsx. A default here would silently override it for every table.
    shallow,
    history = 'push',
    scroll = false,
    throttleMs = 50,
    namespaceUrlKeys = false,
  } = options

  const parsers = useMemo(() => schemaToNuqsParsers(schema), [schema])
  const defaults = useMemo(() => getSchemaDefaults(schema) as T, [schema])

  // Prefix each URL param with `${id}.` so tables sharing the query string
  // (the sidebar carries `location.search` across tabs) don't read each other's
  // filters — e.g. Calls and Facts both have a `type` filter. Opt-in so tables
  // with existing raw-param deep-links keep their param names.
  const urlKeys = useMemo(
    () =>
      namespaceUrlKeys
        ? Object.fromEntries(
            Object.keys(parsers).map((key) => [key, `${id}.${key}`])
          )
        : undefined,
    [namespaceUrlKeys, parsers, id]
  )

  // Use nuqs hook
  const [nuqsState, setNuqsState] = useQueryStates(
    parsers as Record<string, ParserBuilder<unknown>>,
    {
      shallow,
      history,
      scroll,
      throttleMs,
      urlKeys,
    }
  )

  // Store state and version
  const stateRef = useRef<T>(defaults)
  const versionRef = useRef(0)
  const listenersRef = useRef(new Set<() => void>())
  const pausedRef = useRef(false)
  const pendingStateRef = useRef<Partial<T> | null>(null)

  // Cache server snapshot to avoid infinite loop with useSyncExternalStore
  const serverSnapshotRef = useRef<StoreSnapshot<T>>({
    state: { ...defaults, ...initialState } as T,
    version: 0,
  })

  // Compute merged state once per render so validateState isn't called twice
  const currentState = useMemo(() => {
    const validated = validateState(schema, nuqsState) as T
    return { ...defaults, ...initialState, ...validated } as T
  }, [nuqsState, schema, defaults, initialState])

  // Sync ref synchronously so getSnapshot() is always current during this render
  stateRef.current = currentState

  // Notify listeners when state changes
  useEffect(() => {
    stateRef.current = currentState
    versionRef.current++
    listenersRef.current.forEach((listener) => listener())
  }, [currentState])

  // Create stable adapter reference
  const adapter = useMemo<InternalStoreAdapter<T>>(() => {
    return {
      subscribe(listener: () => void) {
        listenersRef.current.add(listener)
        return () => {
          listenersRef.current.delete(listener)
        }
      },

      getSnapshot(): StoreSnapshot<T> {
        return {
          state: stateRef.current,
          version: versionRef.current,
        }
      },

      getServerSnapshot(): StoreSnapshot<T> {
        // Return cached snapshot to avoid infinite loop with useSyncExternalStore
        return serverSnapshotRef.current
      },

      setState(partial: Partial<T>) {
        if (pausedRef.current) {
          pendingStateRef.current = {
            ...pendingStateRef.current,
            ...partial,
          }
          return
        }

        // Convert undefined values to null for nuqs. Also convert empty
        // arrays to null: the parser bridge intentionally skips
        // `.withDefault([])` for empty-array field defaults (see
        // parser-bridge.ts), so nuqs's own clearOnDefault check — which only
        // fires when a parser has a `defaultValue` — never matches an empty
        // array. Left as `[]`, nuqs would serialize it to an empty string
        // and write `?key=` instead of removing the key. Every array field
        // in this schema layer defaults to `[]`, so an empty array always
        // means "no filter" — treat it the same as `undefined`/`null` here.
        const nuqsPartial: Record<string, unknown> = {}
        for (const [key, value] of Object.entries(partial)) {
          nuqsPartial[key] =
            value === undefined || (Array.isArray(value) && value.length === 0)
              ? null
              : value
        }

        setNuqsState((prev) => ({ ...prev, ...nuqsPartial }))
      },

      setField<K extends keyof T>(key: K, value: T[K]) {
        this.setState({ [key]: value } as unknown as Partial<T>)
      },

      reset(fields?: (keyof T)[]) {
        if (fields) {
          const resetPartial: Partial<T> = {}
          for (const field of fields) {
            resetPartial[field] = defaults[field]
          }
          this.setState(resetPartial)
        } else {
          this.setState(defaults)
        }
      },

      pause() {
        pausedRef.current = true
      },

      resume() {
        pausedRef.current = false
        if (pendingStateRef.current) {
          this.setState(pendingStateRef.current)
          pendingStateRef.current = null
        }
      },

      isPaused() {
        return pausedRef.current
      },

      destroy() {
        listenersRef.current.clear()
      },

      getTableId() {
        return id
      },

      getSchema() {
        return schema
      },

      getDefaults() {
        return defaults
      },

      // Internal methods for provider sync
      _syncState(state: T) {
        stateRef.current = state
        versionRef.current++
        listenersRef.current.forEach((listener) => listener())
      },
    }
  }, [id, schema, defaults, setNuqsState])

  return adapter
}

// Re-export parser bridge utilities for advanced use cases
export { schemaToNuqsParsers, createSchemaSerializer } from './parser-bridge'

// Re-export nuqs types for components that need them
// This allows components to import from the adapter layer instead of nuqs directly
export type { ParserBuilder } from 'nuqs'
